import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useState } from "react";
import {
  ContactWithReciprocal,
  CreateRelationshipRow,
  RelationshipRow,
  UpdateRelationshipRow,
} from "../api/relationship.type";
import {
  createContact,
  deleteContact,
  getContactById,
  updateContact,
} from "../api/relationships.api";
import { useAlertsContext } from "../containers/AlertsContextWrapper";
import { useAuthContext } from "../containers/AuthContextWrapper";
import { useToastContext } from "../containers/ToastContextWrapper";
import {
  CONTACT_NOTIFICATION_MESSAGES,
  getContactDisplayName,
} from "../utils/contactNotifications";
import { getErrorMessage, isApiError } from "../utils/errors";
import { useLoadContacts } from "./useLoadContacts";

export const DUPLICATE_CONTACT_TOAST = {
  title: "Contact already exists",
  description: "You already saved someone with that name.",
  type: "error" as const,
};

const isDuplicateContactError = (error: unknown) => {
  if (isApiError(error)) {
    if (error.status === 409) return true;
    const normalized = error.message?.toLowerCase?.();
    return (
      !!normalized &&
      (normalized.includes("duplicate") ||
        normalized.includes("already exists"))
    );
  }

  if (typeof error === "string") {
    const normalized = error.toLowerCase();
    return (
      normalized.includes("duplicate") || normalized.includes("already exists")
    );
  }

  if (typeof error === "object" && error !== null) {
    const normalized = (error as { message?: string }).message?.toLowerCase?.();
    return (
      !!normalized &&
      (normalized.includes("duplicate") ||
        normalized.includes("already exists"))
    );
  }

  return false;
};

export interface ContactsContextType {
  selectedContact?: ContactWithReciprocal;
  isLoading: boolean; // Includes contacts loading, search loading, and CRUD operations
  searchLoading: boolean; // Specifically for contacts/search loading
  setSelectedContact: (contact: ContactWithReciprocal | null) => void;
  update: (contact: UpdateRelationshipRow) => Promise<RelationshipRow>;
  create: (contact: CreateRelationshipRow) => Promise<RelationshipRow>;
  remove: (id: string) => Promise<void>;
  searchContacts: (query: string) => RelationshipRow[];
}

export const ContactsContext = createContext<ContactsContextType>({
  selectedContact: {} as ContactWithReciprocal,
  isLoading: false,
  searchLoading: false,
  setSelectedContact: () => {},
  update: async () => ({ id: "" } as RelationshipRow),
  create: async () => ({ id: "" } as RelationshipRow),
  remove: async () => {},
  searchContacts: () => [],
});

export const ContactsContextWrapper = ({
  children,
  relationshipId,
}: {
  children: React.ReactNode;
  relationshipId?: string;
}) => {
  const queryClient = useQueryClient();
  const { session } = useAuthContext();

  const { contacts, isLoading: isLoadingContacts } = useLoadContacts();

  const { showToast } = useToastContext();
  const { addAlert } = useAlertsContext();

  const [selectedContactState, setSelectedContactState] =
    useState<ContactWithReciprocal | null>(null);

  const { data: selectedContact, isLoading: loadingSelectedContact } = useQuery(
    {
      queryFn: () => getContactById(relationshipId || ""),
      queryKey: ["selectedContact", relationshipId],
      enabled: !!relationshipId && !!session,
    }
  );

  // Use state-based selected contact if available, otherwise use query result
  const currentSelectedContact = selectedContactState || selectedContact;

  const setSelectedContact = useCallback(
    (contact: ContactWithReciprocal | null) => {
      setSelectedContactState(contact);
    },
    []
  );

  // Search contacts
  const searchContacts = useCallback(
    (query: string): RelationshipRow[] => {
      if (!query) return contacts;

      const lowercaseQuery = query.toLowerCase();
      return contacts.filter(
        (contact) =>
          contact.related_user?.first_name
            ?.toLowerCase()
            ?.includes(lowercaseQuery) ||
          contact.related_user?.last_name
            ?.toLowerCase()
            ?.includes(lowercaseQuery) ||
          contact.related_user?.email
            ?.toLowerCase()
            ?.includes(lowercaseQuery) ||
          contact.related_user?.phone?.includes(lowercaseQuery)
      );
    },
    [contacts]
  );

  // Update a contact
  const { mutateAsync: update, isPending: isUpdating } = useMutation({
    mutationFn: updateContact,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["selectedContact"] });
      queryClient.invalidateQueries({ queryKey: [data.id] });

      showToast({
        title: "Contact updated",
        description: "Contact updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating contact:", error);
      showToast({
        title: "Error",
        description: getErrorMessage(error),
        type: "error",
      });
    },
  });

  // Create a new contact
  const { mutateAsync: create, isPending: isCreating } = useMutation({
    mutationFn: createContact,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });

      showToast({
        title: "Contact created",
        description: "Contact created successfully",
      });
    },
    onError: (error) => {
      console.error("Error creating contact:", error);
      if (isDuplicateContactError(error)) {
        showToast(DUPLICATE_CONTACT_TOAST);
        return;
      }

      showToast({
        title: "Error",
        description: getErrorMessage(error),
        type: "error",
      });
    },
  });

  // Delete a contact
  const { mutateAsync: remove } = useMutation({
    mutationFn: deleteContact,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });

      // Find the deleted contact to get their name for the notification
      const deletedContact = contacts.find((contact) => contact.id === id);
      const contactName = deletedContact
        ? getContactDisplayName(deletedContact)
        : "Contact";

      // Trigger notification
      addAlert(
        "Contact Removed",
        CONTACT_NOTIFICATION_MESSAGES.DELETED(contactName),
        "warning"
      );

      showToast({
        title: "Contact deleted",
        description: "Contact deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Error deleting contact:", error);
      showToast({
        title: "Error",
        description: getErrorMessage(error),
        type: "error",
      });
    },
  });

  return (
    <ContactsContext.Provider
      value={{
        selectedContact: currentSelectedContact,
        isLoading:
          isLoadingContacts ||
          isUpdating ||
          isCreating ||
          (!!relationshipId && loadingSelectedContact),
        searchLoading: isLoadingContacts, // Specifically for contacts/search loading
        setSelectedContact,
        update,
        create,
        remove,
        searchContacts,
      }}
    >
      {children}
    </ContactsContext.Provider>
  );
};

export const useContactsContext = () => useContext(ContactsContext);
